import React from "react";
import { Sparkles, TrendingUp, AlertTriangle, ShieldCheck, RefreshCw } from "lucide-react";

const fmt = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const COLOR_TONE = {
  green:  { bg: "rgba(16,185,129,0.10)",  border: "rgba(16,185,129,0.40)",  text: "#34d399" },
  yellow: { bg: "rgba(234,179,8,0.10)",   border: "rgba(234,179,8,0.40)",   text: "#facc15" },
  red:    { bg: "rgba(244,63,94,0.10)",   border: "rgba(244,63,94,0.40)",   text: "#fb7185" },
};

const SEV_COLOR = {
  high: "#fb7185",
  medium: "#facc15",
  low: "#34d399",
};

const RiskSnapshot = ({ snap, onRefresh, refreshing }) => {
  if (!snap) {
    return (
      <div className="border border-white/5 p-5 rounded-lg" style={{ background: "rgba(10,14,23,0.4)" }}>
        <div className="flex items-center justify-between">
          <div className="text-[0.65rem] uppercase tracking-[0.22em] text-cf-gold font-semibold">Risk Snapshot</div>
          <button onClick={onRefresh} disabled={refreshing} className="cf-btn-ghost" style={{ fontSize: "0.7rem", padding: "0.4rem 0.9rem" }}>
            <RefreshCw size={12} /> {refreshing ? "Generating…" : "Generate"}
          </button>
        </div>
        <div className="text-cf-muted text-sm mt-2">No snapshot yet — click Generate.</div>
      </div>
    );
  }

  const tone = COLOR_TONE[snap.color] || COLOR_TONE.yellow;

  return (
    <div
      data-testid="risk-snapshot"
      className="border rounded-lg p-5"
      style={{ background: tone.bg, borderColor: tone.border }}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[0.65rem] uppercase tracking-[0.22em] font-semibold" style={{ color: tone.text }}>
            <Sparkles size={11} className="inline mr-1" style={{ verticalAlign: "-2px" }} /> Risk Snapshot
          </div>
          <div className="font-serif text-2xl mt-1" style={{ color: tone.text }}>
            {snap.verdict}
          </div>
        </div>
        <button onClick={onRefresh} disabled={refreshing} className="cf-btn-ghost" style={{ fontSize: "0.65rem", padding: "0.35rem 0.7rem" }}>
          <RefreshCw size={11} /> {refreshing ? "..." : "Refresh"}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <Stat label="Recommended amount" value={fmt(snap.recommended_amount)} />
        <Stat label="Factor rate" value={`${snap.factor_rate}×`} />
        <Stat label="Suggested term" value={`${snap.term_months} months`} />
        <Stat label="Est. monthly payment" value={fmt(snap.estimated_monthly_payment)} />
      </div>

      {(snap.flags?.length > 0 || snap.strengths?.length > 0) && (
        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          {snap.flags?.length > 0 && (
            <div>
              <div className="text-[0.6rem] uppercase tracking-[0.2em] text-cf-muted mb-2 flex items-center gap-1">
                <AlertTriangle size={10} /> Risk flags
              </div>
              <ul className="space-y-1.5">
                {snap.flags.map((f, i) => (
                  <li key={i} className="text-xs flex items-start gap-2" style={{ color: SEV_COLOR[f.severity] || "#cbd5e1" }}>
                    <span style={{ background: SEV_COLOR[f.severity], width: 4, height: 4, borderRadius: 2, marginTop: 6, flexShrink: 0 }} />
                    <span className="text-cf-text">{f.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {snap.strengths?.length > 0 && (
            <div>
              <div className="text-[0.6rem] uppercase tracking-[0.2em] text-cf-muted mb-2 flex items-center gap-1">
                <ShieldCheck size={10} /> Strengths
              </div>
              <ul className="space-y-1.5">
                {snap.strengths.map((s, i) => (
                  <li key={i} className="text-xs flex items-start gap-2 text-cf-text">
                    <span style={{ background: "#34d399", width: 4, height: 4, borderRadius: 2, marginTop: 6, flexShrink: 0 }} />
                    {s.text}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="text-[0.6rem] uppercase tracking-[0.18em] text-cf-muted mt-4">
        <TrendingUp size={10} className="inline mr-1" style={{ verticalAlign: "-1px" }} />
        Heuristic only — verify with bank statements before issuing terms.
      </div>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[0.6rem] uppercase tracking-[0.2em] text-cf-muted mb-1">{label}</div>
    <div className="text-cf-text font-semibold">{value}</div>
  </div>
);

export default RiskSnapshot;
